import React, { useEffect, useState, useRef, useCallback } from "react";

const FAQTooltip = ({ isOpen, faqItem, onClose, triggerElement }) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState("bottom");
  const [arrowOffset, setArrowOffset] = useState("50%");
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const calculatePosition = useCallback(() => {
    if (!isOpen || !triggerElement || !tooltipRef.current) return;

    const triggerRect = triggerElement.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Temporarily show tooltip to measure dimensions
    const tooltip = tooltipRef.current;
    tooltip.style.visibility = "hidden";
    tooltip.style.display = "block";
    const tooltipRect = tooltip.getBoundingClientRect();
    tooltip.style.visibility = "visible";

    let newPosition = { top: 0, left: 0 };
    let newArrowPosition = "bottom";
    let newArrowOffset = "50%";

    const spacing = isMobile ? 10 : 15;

    // Determine vertical position using viewport coordinates (no scroll offset)
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewport.height - triggerRect.bottom;

    if (spaceAbove >= tooltipRect.height + spacing) {
      // Position above
      newPosition.top = triggerRect.top - tooltipRect.height - spacing;
      newArrowPosition = "bottom";
    } else if (spaceBelow >= tooltipRect.height + spacing) {
      // Position below
      newPosition.top = triggerRect.bottom + spacing;
      newArrowPosition = "top";
    } else {
      // Default to above if no good space
      newPosition.top = triggerRect.top - tooltipRect.height - spacing;
      newArrowPosition = "bottom";
    }

    if (isMobile) {
      // Mobile horizontal positioning
      const maxWidth = Math.min(300, viewport.width - 20);
      
      // Check if trigger is on left or right half of screen
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const viewportCenter = viewport.width / 2;
      
      if (triggerCenter < viewportCenter) {
        // Left side - align tooltip to left edge of trigger
        newPosition.left = Math.max(10, triggerRect.left);
      } else {
        // Right side - align tooltip to right edge of trigger
        newPosition.left = Math.min(
          viewport.width - maxWidth - 10,
          triggerRect.right - maxWidth
        );
      }
      
      // Calculate arrow position to point to trigger center
      const tooltipLeft = newPosition.left;
      const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
      const arrowLeft = triggerCenterX - tooltipLeft;
      
      // Convert to percentage and clamp between 10% and 90%
      const arrowPercent = (arrowLeft / maxWidth) * 100;
      newArrowOffset = Math.max(10, Math.min(90, arrowPercent)) + "%";
      
    } else {
      // Desktop horizontal positioning
      const triggerCenterX = triggerRect.left + (triggerRect.width / 2);
      
      // Try to center tooltip on trigger
      let idealLeft = triggerCenterX - (tooltipRect.width / 2);
      
      // Keep within viewport
      idealLeft = Math.max(
        spacing,
        Math.min(
          viewport.width - tooltipRect.width - spacing,
          idealLeft
        )
      );
      
      newPosition.left = idealLeft;
      
      // Calculate arrow offset to point to trigger center
      const arrowLeft = triggerCenterX - idealLeft;
      const arrowPercent = (arrowLeft / tooltipRect.width) * 100;
      
      // Clamp arrow between 10% and 90% of tooltip width
      newArrowOffset = Math.max(10, Math.min(90, arrowPercent)) + "%";
    }

    setPosition(newPosition);
    setArrowPosition(newArrowPosition);
    setArrowOffset(newArrowOffset);
  }, [isOpen, triggerElement, isMobile]);

  // Position calculation effect
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(calculatePosition);
      }, 0);

      const handleScroll = () => {
        if (isMobile) {
          // On mobile, close tooltip on scroll for better UX
          onClose();
        } else {
          // On desktop, update position
          requestAnimationFrame(calculatePosition);
        }
      };
      
      const handleResize = () => requestAnimationFrame(calculatePosition);

      // Listen to scroll on the popup container instead of window
      const popupContainer = document.querySelector('.donation-popup-container');
      if (popupContainer) {
        popupContainer.addEventListener("scroll", handleScroll, { passive: true });
      }
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timeoutId);
        if (popupContainer) {
          popupContainer.removeEventListener("scroll", handleScroll);
        }
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, calculatePosition, isMobile, onClose]);

  // Click outside and keyboard handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const isTooltipClick = tooltipRef.current?.contains(event.target);
      const isTriggerClick = triggerElement?.contains(event.target);
      
      if (!isTooltipClick && !isTriggerClick) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        triggerElement?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, triggerElement]);

  if (!isOpen || !faqItem) return null;

  return (
    <div
      ref={tooltipRef}
      className={`faq-tooltip faq-tooltip--${arrowPosition} ${isMobile ? 'faq-tooltip--mobile' : 'faq-tooltip--desktop'}`}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        maxWidth: isMobile ? "300px" : "400px",
        width: isMobile ? `${Math.min(300, window.innerWidth - 20)}px` : "auto",
      }}
      role="tooltip"
      aria-labelledby="faq-tooltip-title"
      aria-describedby="faq-tooltip-content"
    >
      <div className="faq-tooltip__content">
        <div className="faq-tooltip__header">
          <h3 id="faq-tooltip-title" className="faq-tooltip__question">
            {faqItem.question}
          </h3>
          <button 
            className="faq-tooltip__close" 
            onClick={onClose} 
            aria-label="Close FAQ tooltip"
            type="button"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div id="faq-tooltip-content" className="faq-tooltip__answer">
          {typeof faqItem.answer === "string" ? (
            <p>{faqItem.answer}</p>
          ) : (
            faqItem.answer
          )}
        </div>
      </div>

      {/* Arrow with dynamic positioning */}
      <div 
        className={`faq-tooltip__arrow faq-tooltip__arrow--${arrowPosition}`}
        style={{
          left: arrowPosition === "top" || arrowPosition === "bottom" ? arrowOffset : undefined
        }}
        aria-hidden="true"
      />
    </div>
  );
};

export default FAQTooltip;